import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  LOAD_WASM,
  NgxScannerQrcodeComponent,
  NgxScannerQrcodeService,
  ScannerQRCodeConfig,
  ScannerQRCodeResult,
  ScannerQRCodeSelectedFiles,
  ScannerQRCodeSymbolType
} from 'ngx-scanner-qrcode';
import { SiteStore } from 'src/app/store/site.store';
import { QRPaymentEnum } from 'src/app/store/types';
import { environment } from 'src/environments/environment';
import { CloseIconComponent } from '../icons/close-icon.component';
import { delay } from 'rxjs';
import { Router } from '@angular/router';
import { QrTransferStore } from 'src/app/store/qr-transfer.store';

LOAD_WASM('assets/wasm/ngx-scanner-qrcode.wasm').subscribe();

@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, NgxScannerQrcodeComponent, CloseIconComponent],
  templateUrl: './qr-scanner.component.html',
  styleUrl: './qr-scanner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QrScannerComponent implements AfterViewInit, OnDestroy {
  siteStore = inject(SiteStore);
  qrTransferStore = inject(QrTransferStore);

  started = signal(false);
  private _isUsingBackCamera = true; // Track the current camera (back by default)

  // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia#front_and_back_camera
  public config: ScannerQRCodeConfig = {
    constraints: {
      video: {
        width: window.innerWidth,
        aspectRatio: { exact: 0.7 } // 16:9
      }
    },
    symbolType: [ScannerQRCodeSymbolType.ScannerQRCode_QRCODE],
    isBeep: true
    // isMasked: false,
    // unScan: false
    // canvasStyles: [
    //   { /* layer */
    //     lineWidth: 1,
    //     fillStyle: '#00950685',
    //     strokeStyle: '#00950685',
    //   },
    //   { /* text */
    //     font: '17px serif',
    //     fillStyle: '#ff0000',
    //     strokeStyle: '#ff0000',
    //   }
    // ],
  };

  public qrCodeResult: ScannerQRCodeSelectedFiles[] = [];
  public qrCodeResult2: ScannerQRCodeSelectedFiles[] = [];

  @ViewChild('action') action!: NgxScannerQrcodeComponent;

  constructor(
    private router: Router,
    private qrcode: NgxScannerQrcodeService,
    private matSnackBar: MatSnackBar
  ) {}
  ngOnDestroy(): void {
    this.handle(this.action, 'stop');
    document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  ngAfterViewInit(): void {
    this.action.isReady.subscribe((res: any) => {
      this.handle(this.action, 'start');
    });
    document.addEventListener('keydown', this.handleEscapeKey.bind(this));
  }

  handleEscapeKey(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.started()) {
      this.close();
    }
  }

  public toggleCamera(): void {
    this.action.devices.subscribe((devices: any[]) => {
      const backCamera = devices.find((f) => /back|rear|environment/gi.test(f.label));
      const frontCamera = devices.find((f) => /front|user/gi.test(f.label));

      const selectedDevice = this._isUsingBackCamera ? frontCamera?.deviceId : backCamera?.deviceId;

      if (selectedDevice) {
        this.action.playDevice(selectedDevice);
        this._isUsingBackCamera = !this._isUsingBackCamera; // Toggle the camera
      } else {
        this.matSnackBar.open('No se encontró la cámara seleccionada.', 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
      }
    });
  }

  close(handleStop = true) {
    try {
      if (handleStop) {
        this.handle(this.action, 'stop');
      }
      this.started.set(false);
      this.siteStore.toggleShowQrScanner();
    } catch (err) {}
  }

  public onEvent(e: ScannerQRCodeResult[], action?: any): void {
    if (e.length > 0) {
      const event = e[0];
      if (event.typeName === 'ZBAR_QRCODE' && event.value) {
        let type: QRPaymentEnum, id;
        const text = event.value;

        if (text.startsWith(`${environment.app_url}/pay/`)) {
          type = QRPaymentEnum.GENERATED_QR;
          id = text.split(`${environment.app_url}/pay/`).pop();
        } else if (text.startsWith(`${environment.app_url}/payto/`)) {
          type = QRPaymentEnum.ACCOUNT_QR;
          id = text.split(`${environment.app_url}/payto/`).pop();
        }
        // else if (text.startsWith(`${environment.app_url}/paylink/`)) {
        // 	const id = text.split(`${environment.app_url}/paylink/`)[1];
        // 	this.newPaymentStore.manageLinkPayment(id);
        // 	this.close()
        // 	return
        // }

        if (!type) {
          this.matSnackBar.open(`Código QR Inválido`, 'Ok', {
            duration: 4000,
            panelClass: 'mat-snack-error'
          });
          this.close();
        } else {
          this.close();
          this.qrTransferStore.manageQrPayment(type, id);
        }
      }
    }
  }

  public handle(action: any, fn: string): void {
    try {
      const playDeviceFacingBack = (devices: any[]) => {
        // front camera or back camera check here!
        const device = devices.find((f) => /back|rear|environment/gi.test(f.label)); // Default Back Facing Camera
        action.playDevice(device ? device.deviceId : devices[0].deviceId);
      };
      if (fn === 'start') {
        action[fn](playDeviceFacingBack).subscribe(
          (r: any) => {
            this.handleStart(r);
          },
          (err) => {
            this.close(false);
            if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
              this.matSnackBar.open(
                'Permiso denegado para acceder a la cámara. Por favor, habilite el permiso en la configuración de su navegador.',
                'Ok',
                {
                  duration: 10000,
                  panelClass: 'mat-snack-error'
                }
              );
            } else {
              console.error('Error al iniciar el escáner:', err);
            }
          }
        );
      } else {
        action[fn]().subscribe((r: any) => {
          // console.log(fn, r);
        });
      }
    } catch (err) {
      // console.error(err);
    }
  }

  handleStart(r) {
    const interval = setInterval(() => {
      if (!this.action.isLoading) {
        clearInterval(interval);
        this.started.set(true);
      }
    }, 1000);
  }
}
